import {
    Button,
    CircularProgress,
    Grid,
    IconButton,
    LinearProgress,
    makeStyles,
    Typography,
} from "@material-ui/core"
import { AddPhotoAlternate, DeleteForever } from "@material-ui/icons"
import { useSnackbar } from "../../utils/notificacoes"
import { useEffect, useRef, useState } from "react"

import { redimensionarMantendoAspecto } from "../../functions/imagens"
import { cadastrarImagem, ExcluirImagem, ObterImagens } from "../../services/cardapio"
import Cabecalho from "../Cabecalho"

const useStyles = makeStyles(theme => ({
    wrapper: {
        textAlign: "left",
        maxWidth: 1100,
        margin: "0 auto",
        padding: "0 4px 32px",
    },
    header: {
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
        gap: 12,
        flexWrap: "wrap",
        marginBottom: 20,
    },
    titulo: {
        fontWeight: 700,
        color: "#2c3e50",
        lineHeight: 1.2,
    },
    subtitulo: {
        color: "#6b7280",
        marginTop: 2,
    },
    botaoAdicionar: {
        borderRadius: 25,
        textTransform: "none",
        fontWeight: 600,
        padding: "8px 22px",
        backgroundColor: "var(--cor-botao-salvar)",
        color: "#fff",
        boxShadow: "0 4px 15px rgba(var(--cor-botao-salvar-rgb),0.3)",
        transition: "all 0.2s ease",
        "&:hover": {
            backgroundColor: "var(--cor-botao-salvar-hover)",
            transform: "translateY(-2px)",
            boxShadow: "0 6px 20px rgba(var(--cor-botao-salvar-rgb),0.4)",
        },
        [theme.breakpoints.down("xs")]: {
            width: "100%",
        },
    },
    envioBox: {
        marginBottom: 20,
        padding: "12px 16px",
        borderRadius: 12,
        backgroundColor: "#f8f9fa",
        border: "1px solid #e9ecef",
    },
    barraProgresso: {
        marginTop: 8,
        height: 8,
        borderRadius: 6,
    },
    tile: {
        position: "relative",
        borderRadius: 12,
        overflow: "hidden",
        border: "1px solid #e9ecef",
        backgroundColor: "#fafafa",
        boxShadow: "0 2px 10px rgba(0,0,0,0.06)",
        transition: "transform 0.2s ease, box-shadow 0.2s ease",
        "&:hover": {
            transform: "translateY(-3px)",
            boxShadow: "0 8px 24px rgba(0,0,0,0.12)",
        },
        "&:hover $botaoExcluir": {
            opacity: 1,
        },
    },
    foto: {
        display: "block",
        width: "100%",
        aspectRatio: "1 / 1",
        objectFit: "cover",
    },
    botaoExcluir: {
        position: "absolute",
        top: 6,
        right: 6,
        padding: 5,
        backgroundColor: "rgba(255,255,255,0.92)",
        boxShadow: "0 2px 6px rgba(0,0,0,0.2)",
        opacity: 0,
        transition: "opacity 0.2s ease",
        "&:hover": {
            backgroundColor: "#fff",
        },
        [theme.breakpoints.down("sm")]: {
            opacity: 1,
        },
    },
    vazio: {
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "center",
        gap: 8,
        padding: "48px 24px",
        borderRadius: 16,
        border: "2px dashed #cfd8dc",
        backgroundColor: "#f8f9fa",
        color: "#6b7280",
        cursor: "pointer",
        transition: "all 0.2s ease",
        "&:hover": {
            borderColor: "#4caf50",
            backgroundColor: "#f4fbf4",
        },
    },
    vazioIcone: {
        fontSize: 48,
        color: "#b0bec5",
    },
}))

/**
 * Conteúdo da tela Galeria: grade das fotos cadastradas da operação, com opção de
 * excluir (checa uso em Produto/OpcaoProduto no backend) e adicionar várias de uma vez.
 */
export default function GaleriaConteudo({ estabelecimento }) {
    const classes = useStyles()
    const { enqueueSnackbar } = useSnackbar()
    const idEstabelecimento = estabelecimento?.id

    const [imagens, setImagens] = useState([])
    const [carregando, setCarregando] = useState(false)
    const [envio, setEnvio] = useState(null) // { feitos, total } durante o upload
    const fileInputRef = useRef(null)

    const autorizacao = () => localStorage.getItem("chaveSistema")

    const carregar = async () => {
        setCarregando(true)
        try {
            const resp = await ObterImagens(idEstabelecimento, autorizacao())
            if (resp?.retornoErro) {
                enqueueSnackbar(resp.mensagem || "Não foi possível carregar as fotos.", {
                    variant: "error",
                })
                setImagens([])
            } else {
                const urls = (Array.isArray(resp) ? resp : [])
                    .map(i => (typeof i === "string" ? i : i?.valor))
                    .filter(Boolean)
                setImagens(urls)
            }
        } catch (error) {
            enqueueSnackbar("Erro ao carregar as fotos.", { variant: "error" })
        } finally {
            setCarregando(false)
        }
    }

    useEffect(() => {
        if (idEstabelecimento) carregar()
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [idEstabelecimento])

    const handleArquivos = async event => {
        const files = Array.from(event.target.files || [])
        event.target.value = "" // permite reenviar os mesmos arquivos depois
        if (files.length === 0) return

        setEnvio({ feitos: 0, total: files.length })
        let ok = 0
        for (let i = 0; i < files.length; i++) {
            try {
                const arquivo = await redimensionarMantendoAspecto(files[i])
                const resp = await cadastrarImagem(
                    idEstabelecimento,
                    arquivo.name,
                    null,
                    null,
                    arquivo,
                    autorizacao()
                )
                if (resp && !resp.retornoErro) ok++
            } catch (error) {
                console.error(error)
            }
            setEnvio({ feitos: i + 1, total: files.length })
        }
        setEnvio(null)

        enqueueSnackbar(`${ok} de ${files.length} foto(s) enviada(s).`, {
            variant: ok === files.length ? "success" : ok > 0 ? "warning" : "error",
        })
        await carregar()
    }

    const handleExcluir = async url => {
        if (!window.confirm("Excluir esta foto?")) return
        const resp = await ExcluirImagem(idEstabelecimento, url, autorizacao())
        if (resp?.retornoErro) {
            enqueueSnackbar(resp.mensagem || "Não foi possível excluir a foto.", {
                variant: "warning",
            })
        } else {
            enqueueSnackbar(resp?.mensagem || "Foto excluída.", { variant: "success" })
            setImagens(prev => prev.filter(u => u !== url))
        }
    }

    return (
        <>
            <Cabecalho pagina={"Galeria"} telasEdicao />
            <div className={classes.wrapper}>
                <div className={classes.header}>
                    <div>
                        <Typography variant="h5" className={classes.titulo}>
                            Fotos para o Cardápio
                        </Typography>
                        <Typography variant="body2" className={classes.subtitulo}>
                            {imagens.length > 0
                                ? `${imagens.length} foto${imagens.length > 1 ? "s" : ""} cadastrada${
                                      imagens.length > 1 ? "s" : ""
                                  }`
                                : "Envie imagens para usar nos produtos e opções"}
                        </Typography>
                    </div>
                    <Button
                        variant="contained"
                        disableElevation
                        startIcon={<AddPhotoAlternate />}
                        onClick={() => fileInputRef.current && fileInputRef.current.click()}
                        disabled={!!envio}
                        className={classes.botaoAdicionar}
                    >
                        Adicionar fotos
                    </Button>
                    <input
                        ref={fileInputRef}
                        type="file"
                        accept="image/*"
                        multiple
                        style={{ display: "none" }}
                        onChange={handleArquivos}
                    />
                </div>

                {envio && (
                    <div className={classes.envioBox}>
                        <Typography variant="body2" color="textSecondary">
                            Enviando {envio.feitos}/{envio.total}...
                        </Typography>
                        <LinearProgress
                            variant="determinate"
                            value={(envio.feitos / envio.total) * 100}
                            className={classes.barraProgresso}
                        />
                    </div>
                )}

                {carregando ? (
                    <Grid container justifyContent="center" style={{ padding: 48 }}>
                        <CircularProgress style={{ color: "#4caf50" }} />
                    </Grid>
                ) : imagens.length === 0 ? (
                    <div
                        className={classes.vazio}
                        onClick={() => fileInputRef.current && fileInputRef.current.click()}
                    >
                        <AddPhotoAlternate className={classes.vazioIcone} />
                        <Typography variant="subtitle1" style={{ fontWeight: 600 }}>
                            Nenhuma foto cadastrada
                        </Typography>
                        <Typography variant="body2">
                            Clique aqui para adicionar suas primeiras fotos
                        </Typography>
                    </div>
                ) : (
                    <Grid container spacing={2}>
                        {imagens.map((url, idx) => (
                            <Grid item xs={6} sm={4} md={3} key={idx}>
                                <div className={classes.tile}>
                                    <img
                                        src={url}
                                        alt=""
                                        loading="lazy"
                                        className={classes.foto}
                                    />
                                    <IconButton
                                        size="small"
                                        title="Excluir foto"
                                        onClick={() => handleExcluir(url)}
                                        className={classes.botaoExcluir}
                                    >
                                        <DeleteForever style={{ color: "#DC4C64", fontSize: 18 }} />
                                    </IconButton>
                                </div>
                            </Grid>
                        ))}
                    </Grid>
                )}
            </div>
        </>
    )
}
